import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Ticket, Plus, Pencil, Trash2, Calendar } from "lucide-react";
import { toast } from "sonner";
import { LockedModule } from "./SchoolAnalytics";

const EMPTY = { title: "", description: "", exam_type: "GENEL", grade_level: "8", start_at: "", end_at: "", duration_minutes: 60, question_ids: "", negative_marking: 0.25, published: true, show_answers: true };

export default function SchoolMockExams() {
  const [modules, setModules] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const mod = modules.find((m) => m.key === "school_mock_exams");
  const owned = mod?.owned;

  const load = async () => {
    const [m, my] = await Promise.all([
      api.get(`${V1}/school-modules`),
      api.get(`${V1}/school/mock-exams/mine`).catch(() => ({ data: [] })),
    ]);
    setModules(m.data || []);
    setItems(my.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        duration_minutes: parseInt(form.duration_minutes),
        negative_marking: parseFloat(form.negative_marking),
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        question_ids: (form.question_ids || "").split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (editing) {
        await api.patch(`${V1}/school/mock-exams/${editing.id}`, payload);
      } else {
        await api.post(`${V1}/school/mock-exams`, payload);
      }
      toast.success("Kaydedildi");
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Sınavı silmek istiyor musunuz?")) return;
    await api.delete(`${V1}/school/mock-exams/${id}`);
    toast.success("Silindi"); load();
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      title: e.title, description: e.description || "", exam_type: e.exam_type || "GENEL",
      grade_level: e.grade_level || "8",
      start_at: e.start_at?.slice(0, 16), end_at: e.end_at?.slice(0, 16),
      duration_minutes: e.duration_minutes, question_ids: (e.question_ids || []).join(","),
      negative_marking: e.negative_marking ?? 0.25, published: e.published !== false,
      show_answers: e.show_answers !== false,
    });
    setOpen(true);
  };

  if (!mod) return <div className="text-muted-foreground">Yükleniyor…</div>;
  if (!owned) return <LockedModule mod={mod} testid="mock-exams-locked" />;

  return (
    <div className="space-y-6" data-testid="school-mock-exams-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Modül · {mod.label}</div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Ticket className="h-7 w-7 text-primary" /> Kendi Deneme Sınavlarım</h1>
          <p className="text-muted-foreground text-sm mt-1">Okulunuza özel deneme sınavı oluşturun.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="new-school-exam-btn">
          <Plus className="h-4 w-4" /> Yeni Deneme
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.length === 0 && <div className="col-span-2 p-10 rounded-xl border border-dashed border-border text-center text-muted-foreground">Henüz deneme yok.</div>}
        {items.map((e) => (
          <div key={e.id} className="p-5 rounded-xl border border-border bg-card" data-testid={`school-exam-row-${e.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(e.start_at).toLocaleString("tr-TR")}</div>
                <div className="font-display font-bold text-lg mt-1">{e.title}</div>
                <div className="text-sm text-muted-foreground line-clamp-2">{e.description}</div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-0.5 bg-muted rounded">Süre: {e.duration_minutes}dk</span>
                  <span className="px-2 py-0.5 bg-muted rounded">Sınıf: {e.grade_level}</span>
                  <span className="px-2 py-0.5 bg-muted rounded">{e.question_count || (e.question_ids || []).length} soru</span>
                  {!e.published && <span className="px-2 py-0.5 bg-muted rounded">Taslak</span>}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(e)} data-testid={`edit-school-exam-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(e.id)} data-testid={`delete-school-exam-${e.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Sınavı Düzenle" : "Yeni Deneme Sınavı"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Başlık *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="school-exam-title" /></div>
            <div><Label>Açıklama</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Sınıf</Label><Input value={form.grade_level} onChange={(e) => setForm({ ...form, grade_level: e.target.value })} /></div>
              <div><Label>Süre (dk)</Label><Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Başlangıç</Label><Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} /></div>
              <div><Label>Bitiş</Label><Input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} /></div>
            </div>
            <div>
              <Label>Soru ID&apos;leri (virgülle ayır)</Label>
              <Textarea rows={2} value={form.question_ids} onChange={(e) => setForm({ ...form, question_ids: e.target.value })} placeholder="qid1, qid2, ..." />
              <div className="text-[10px] text-muted-foreground mt-1">Soruları admin panelinden alabilirsiniz.</div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
              Yayında
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} disabled={busy || !form.title || !form.start_at || !form.end_at} data-testid="save-school-exam-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
